<script setup>
/* global axios */
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { DirectUpload } from 'activestorage';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import IntegrationsAPI from 'dashboard/api/integrations';
import { setDirectUploadAuthHeaders } from 'dashboard/helper/directUploadsHelper';
import {
  buildTemplatePreview,
  convertVariableFormat,
  filterTemplatesByChannel,
  hasInvalidVariables,
  insertAtSelection,
  MAX_TEMPLATE_BUTTONS,
  MEDIA_RULES,
  mediaFileError,
  nextBodyVariable,
  PARAMETER_FORMATS,
  renderWhatsappFormatting,
  templateNameError,
  variableIdentifiers,
} from './whatsappTemplateManagerHelpers';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const isLoading = ref(true);
const isSaving = ref(false);
const isSyncing = ref(false);
const manager = ref({
  administrator: false,
  channels: [],
  available_channels: [],
  templates: [],
  storage: {
    provider: 'gcs',
    enabled: false,
    credentials_configured: false,
  },
});
const showEditor = ref(false);
const showStorageSettings = ref(false);
const editingTemplateId = ref(null);
const selectedFile = ref(null);
const localMediaUrl = ref('');
const mediaValidationError = ref('');
const showEmojiPicker = ref(false);
const syncChannelId = ref('');
const filterChannelId = ref('all');
const isSavingStorage = ref(false);
const credentialsFileName = ref('');
const credentialsClientEmail = ref('');
const previewDialogRef = ref(null);
const selectedPreviewTemplate = ref(null);

const emptyStorageForm = () => ({
  enabled: false,
  project_id: '',
  bucket_name: '',
  credentials_json: '',
});

const storageForm = ref(emptyStorageForm());

const emptyForm = () => ({
  channel_id: '',
  name: '',
  language: 'es',
  category: 'UTILITY',
  template_kind: 'text',
  configuration: {
    template_type: 'default',
    header_type: 'none',
    parameter_format: PARAMETER_FORMATS.POSITIONAL,
    header_text: '',
    header_example: '',
    body: '',
    body_examples: [],
    footer: '',
    buttons: [],
    flow_id: '',
    navigate_screen: '',
    flow_action: 'navigate',
    flow_cta: '',
  },
});

const form = ref(emptyForm());

const categoryOptions = computed(() => [
  {
    value: 'UTILITY',
    label: t('WHATSAPP_TEMPLATE_MANAGER.CATEGORIES.UTILITY'),
  },
  {
    value: 'MARKETING',
    label: t('WHATSAPP_TEMPLATE_MANAGER.CATEGORIES.MARKETING'),
  },
]);

const headerTypeOptions = computed(() => [
  { value: 'none', label: t('WHATSAPP_TEMPLATE_MANAGER.HEADER_TYPES.NONE') },
  { value: 'text', label: t('WHATSAPP_TEMPLATE_MANAGER.HEADER_TYPES.TEXT') },
  { value: 'image', label: t('WHATSAPP_TEMPLATE_MANAGER.TYPES.IMAGE') },
  { value: 'video', label: t('WHATSAPP_TEMPLATE_MANAGER.TYPES.VIDEO') },
  {
    value: 'document',
    label: t('WHATSAPP_TEMPLATE_MANAGER.TYPES.DOCUMENT'),
  },
]);

const parameterFormatOptions = computed(() => [
  {
    value: PARAMETER_FORMATS.POSITIONAL,
    label: t('WHATSAPP_TEMPLATE_MANAGER.FORM.VARIABLE_TYPE_NUMBER'),
  },
  {
    value: PARAMETER_FORMATS.NAMED,
    label: t('WHATSAPP_TEMPLATE_MANAGER.FORM.VARIABLE_TYPE_NAME'),
  },
]);

const supportedLanguageOptions = [
  { value: 'es', label: 'Español' },
  { value: 'es_AR', label: 'Español (Argentina)' },
  { value: 'es_CO', label: 'Español (Colombia)' },
  { value: 'es_ES', label: 'Español (España)' },
  { value: 'es_MX', label: 'Español (México)' },
  { value: 'es_PA', label: 'Español (Panamá)' },
  { value: 'en_US', label: 'English (United States)' },
];

const languageOptions = computed(() => {
  const currentLanguage = form.value.language;
  if (
    !editingTemplateId.value ||
    supportedLanguageOptions.some(option => option.value === currentLanguage)
  ) {
    return supportedLanguageOptions;
  }

  return [
    ...supportedLanguageOptions,
    {
      value: currentLanguage,
      label: `${currentLanguage} (${t(
        'WHATSAPP_TEMPLATE_MANAGER.FORM.LEGACY_LANGUAGE'
      )})`,
    },
  ];
});

const creatableChannels = computed(() =>
  manager.value.channels.filter(channel => channel.permissions.can_create)
);

const syncableChannels = computed(() =>
  manager.value.channels.filter(channel => channel.permissions.can_submit)
);

const channelOptions = computed(() =>
  creatableChannels.value.map(channel => ({
    value: channel.id,
    label: channel.inbox.name,
  }))
);

const syncChannelOptions = computed(() =>
  syncableChannels.value.map(channel => ({
    value: channel.id,
    label: channel.inbox.name,
  }))
);

const filterChannelOptions = computed(() => [
  {
    value: 'all',
    label: t('WHATSAPP_TEMPLATE_MANAGER.FILTERS.ALL_CHANNELS'),
  },
  ...manager.value.channels.map(channel => ({
    value: channel.id,
    label: channel.inbox.name,
  })),
]);

const selectedChannel = computed(() =>
  manager.value.channels.find(
    channel => Number(channel.id) === Number(form.value.channel_id)
  )
);

const canSubmitSelectedChannel = computed(
  () => selectedChannel.value?.permissions?.can_submit
);

const buttonTypeOptions = computed(() => [
  {
    value: 'QUICK_REPLY',
    label: t('WHATSAPP_TEMPLATE_MANAGER.BUTTON_TYPES.QUICK_REPLY'),
  },
  {
    value: 'URL',
    label: t('WHATSAPP_TEMPLATE_MANAGER.BUTTON_TYPES.URL'),
  },
  {
    value: 'PHONE_NUMBER',
    label: t('WHATSAPP_TEMPLATE_MANAGER.BUTTON_TYPES.PHONE_NUMBER'),
  },
  {
    value: 'VOICE_CALL',
    label: selectedChannel.value?.capabilities?.voice_call
      ? t('WHATSAPP_TEMPLATE_MANAGER.BUTTON_TYPES.VOICE_CALL')
      : t('WHATSAPP_TEMPLATE_MANAGER.BUTTON_TYPES.VOICE_CALL_UNAVAILABLE'),
    disabled: !selectedChannel.value?.capabilities?.voice_call,
  },
  {
    value: 'COPY_CODE',
    label: t('WHATSAPP_TEMPLATE_MANAGER.BUTTON_TYPES.COPY_CODE'),
    disabled: form.value.category !== 'MARKETING',
  },
  {
    value: 'REQUEST_CONTACT_INFO',
    label: t('WHATSAPP_TEMPLATE_MANAGER.BUTTON_TYPES.REQUEST_CONTACT_INFO'),
  },
]);

const templateTypeOptions = computed(() => [
  {
    value: 'default',
    icon: 'i-lucide-layout-template',
    title: t('WHATSAPP_TEMPLATE_MANAGER.TEMPLATE_TYPES.DEFAULT'),
    description: t(
      'WHATSAPP_TEMPLATE_MANAGER.TEMPLATE_TYPES.DEFAULT_DESCRIPTION'
    ),
    disabled: false,
  },
  {
    value: 'catalog',
    icon: 'i-lucide-shopping-bag',
    title: t('WHATSAPP_TEMPLATE_MANAGER.TEMPLATE_TYPES.CATALOG'),
    description: t(
      'WHATSAPP_TEMPLATE_MANAGER.TEMPLATE_TYPES.CATALOG_DESCRIPTION'
    ),
    disabled: false,
  },
  {
    value: 'flow',
    icon: 'i-lucide-list-checks',
    title: t('WHATSAPP_TEMPLATE_MANAGER.TEMPLATE_TYPES.FLOW'),
    description: t('WHATSAPP_TEMPLATE_MANAGER.TEMPLATE_TYPES.FLOW_DESCRIPTION'),
    disabled: false,
  },
  {
    value: 'call_permission_request',
    icon: 'i-lucide-phone-call',
    title: t('WHATSAPP_TEMPLATE_MANAGER.TEMPLATE_TYPES.CALL_PERMISSION'),
    description: selectedChannel.value?.capabilities?.voice_call
      ? t(
          'WHATSAPP_TEMPLATE_MANAGER.TEMPLATE_TYPES.CALL_PERMISSION_DESCRIPTION'
        )
      : t(
          'WHATSAPP_TEMPLATE_MANAGER.TEMPLATE_TYPES.CALL_PERMISSION_UNAVAILABLE'
        ),
    disabled: !selectedChannel.value?.capabilities?.voice_call,
  },
]);

const parameterFormat = computed(
  () =>
    form.value.configuration.parameter_format || PARAMETER_FORMATS.POSITIONAL
);

const bodyVariables = computed(() =>
  variableIdentifiers(form.value.configuration.body, parameterFormat.value)
);

const headerVariables = computed(() =>
  variableIdentifiers(
    form.value.configuration.header_text,
    parameterFormat.value
  )
);

const headerHasVariable = computed(() => headerVariables.value.length > 0);

const hasVariableFormatError = computed(() => {
  const configuration = form.value.configuration;
  const headerVariableOccurrences =
    configuration.header_text.match(/\{\{[^{}]+\}\}/g) || [];
  if (
    hasInvalidVariables(configuration.body, parameterFormat.value) ||
    hasInvalidVariables(configuration.header_text, parameterFormat.value) ||
    headerVariableOccurrences.length > 1
  ) {
    return true;
  }

  if (parameterFormat.value === PARAMETER_FORMATS.NAMED) return false;

  const headerNumber = headerVariables.value[0];
  return (
    (headerNumber !== undefined && headerNumber !== 1) ||
    bodyVariables.value.some((number, index) => number !== index + 1)
  );
});

const acceptedMediaTypes = computed(
  () => MEDIA_RULES[form.value.template_kind]?.accept || ''
);

const mediaReference = computed(() => {
  const references = {
    image: t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_IMAGE_REFERENCE'),
    video: t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_VIDEO_REFERENCE'),
    document: t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_DOCUMENT_REFERENCE'),
  };
  return references[form.value.template_kind] || '';
});

const nameValidationMessage = computed(() => {
  const error = templateNameError(form.value.name);
  const errors = {
    required: t('WHATSAPP_TEMPLATE_MANAGER.FORM.NAME_ERRORS.REQUIRED'),
    format: t('WHATSAPP_TEMPLATE_MANAGER.FORM.NAME_ERRORS.FORMAT'),
    length: t('WHATSAPP_TEMPLATE_MANAGER.FORM.NAME_ERRORS.LENGTH'),
  };
  return errors[error] || t('WHATSAPP_TEMPLATE_MANAGER.FORM.NAME_HELP');
});

const hasNameError = computed(() =>
  Boolean(templateNameError(form.value.name))
);

const formValidationMessage = computed(() => {
  if (!form.value.channel_id) {
    return t('WHATSAPP_TEMPLATE_MANAGER.FORM.ERRORS.CHANNEL');
  }
  if (hasNameError.value) return nameValidationMessage.value;
  if (!form.value.configuration.body.trim()) {
    return t('WHATSAPP_TEMPLATE_MANAGER.FORM.ERRORS.BODY');
  }
  if (hasVariableFormatError.value) {
    return t('WHATSAPP_TEMPLATE_MANAGER.FORM.ERRORS.VARIABLE_FORMAT', {
      numberExample: '{{1}}',
      nameExample: '{{customer_name}}',
    });
  }
  if (
    form.value.configuration.header_type === 'text' &&
    !form.value.configuration.header_text.trim()
  ) {
    return t('WHATSAPP_TEMPLATE_MANAGER.FORM.ERRORS.HEADER');
  }
  if (
    bodyVariables.value.some(
      (_number, index) => !form.value.configuration.body_examples[index]?.trim()
    )
  ) {
    return t('WHATSAPP_TEMPLATE_MANAGER.FORM.ERRORS.BODY_EXAMPLES');
  }
  if (
    headerHasVariable.value &&
    !form.value.configuration.header_example.trim()
  ) {
    return t('WHATSAPP_TEMPLATE_MANAGER.FORM.ERRORS.HEADER_EXAMPLE');
  }
  if (
    form.value.configuration.template_type === 'flow' &&
    (!form.value.configuration.flow_id.trim() ||
      !form.value.configuration.navigate_screen.trim() ||
      !form.value.configuration.flow_cta.trim())
  ) {
    return t('WHATSAPP_TEMPLATE_MANAGER.FORM.ERRORS.FLOW');
  }
  return '';
});

const currentTemplatePreview = computed(() =>
  buildTemplatePreview(form.value.configuration)
);

const previewBodyHtml = computed(() =>
  renderWhatsappFormatting(currentTemplatePreview.value.body)
);

const previewButtonLabels = preview => {
  const templateType = preview.templateType;
  if (templateType === 'catalog') {
    return [t('WHATSAPP_TEMPLATE_MANAGER.FORM.CATALOG_BUTTON_PREVIEW')];
  }
  if (templateType === 'flow') {
    return [
      preview.flowCta ||
        t('WHATSAPP_TEMPLATE_MANAGER.FORM.FLOW_BUTTON_PREVIEW'),
    ];
  }
  if (templateType === 'call_permission_request') {
    return [t('WHATSAPP_TEMPLATE_MANAGER.FORM.CALL_PERMISSION_PREVIEW')];
  }
  return preview.buttons.map(
    button =>
      button.text ||
      (button.type === 'COPY_CODE'
        ? t('WHATSAPP_TEMPLATE_MANAGER.BUTTON_TYPES.COPY_CODE')
        : t('WHATSAPP_TEMPLATE_MANAGER.BUTTON_TYPES.REQUEST_CONTACT_INFO'))
  );
};

const previewButtons = computed(() =>
  previewButtonLabels(currentTemplatePreview.value)
);

const selectedTemplatePreview = computed(() =>
  buildTemplatePreview(selectedPreviewTemplate.value?.configuration)
);

const selectedPreviewBodyHtml = computed(() =>
  renderWhatsappFormatting(selectedTemplatePreview.value.body)
);

const selectedPreviewButtons = computed(() =>
  previewButtonLabels(selectedTemplatePreview.value)
);

const previewDialogTitle = computed(() =>
  t('WHATSAPP_TEMPLATE_MANAGER.PREVIEW.TITLE', {
    name: selectedPreviewTemplate.value?.name || '',
  })
);

const resetMediaPreview = () => {
  if (localMediaUrl.value) URL.revokeObjectURL(localMediaUrl.value);
  localMediaUrl.value = '';
  selectedFile.value = null;
  mediaValidationError.value = '';
};

const templates = computed(() =>
  filterTemplatesByChannel(manager.value.templates, filterChannelId.value)
);
const bodyHelp = computed(() =>
  t('WHATSAPP_TEMPLATE_MANAGER.FORM.BODY_HELP', {
    examples:
      parameterFormat.value === PARAMETER_FORMATS.NAMED
        ? '{{customer_name}}, {{course_name}}'
        : '{{1}}, {{2}}',
  })
);
const editorTitle = computed(() =>
  editingTemplateId.value
    ? t('WHATSAPP_TEMPLATE_MANAGER.FORM.TITLE_EDIT')
    : t('WHATSAPP_TEMPLATE_MANAGER.FORM.TITLE_NEW')
);

watch(
  bodyVariables,
  variables => {
    const existing = form.value.configuration.body_examples;
    form.value.configuration.body_examples = variables.map(
      (_number, index) => existing[index] || ''
    );
  },
  { deep: true }
);

watch(
  () => form.value.template_kind,
  kind => {
    if (kind !== 'text') {
      form.value.configuration.header_text = '';
      form.value.configuration.header_example = '';
    }
  }
);

watch(
  () => form.value.configuration.header_type,
  headerType => {
    form.value.template_kind = ['image', 'video', 'document'].includes(
      headerType
    )
      ? headerType
      : 'text';
    if (headerType !== 'text') {
      form.value.configuration.header_text = '';
      form.value.configuration.header_example = '';
    }
    if (!['image', 'video', 'document'].includes(headerType)) {
      resetMediaPreview();
    }
  }
);

watch(
  () => form.value.configuration.template_type,
  templateType => {
    if (templateType !== 'default') {
      form.value.configuration.header_type = ['none', 'text'].includes(
        form.value.configuration.header_type
      )
        ? form.value.configuration.header_type
        : 'none';
      form.value.configuration.buttons = [];
    }
    if (['catalog', 'call_permission_request'].includes(templateType)) {
      form.value.category = 'MARKETING';
    }
  }
);

const fetchManager = async () => {
  isLoading.value = true;
  try {
    const { data } = await IntegrationsAPI.getWhatsappTemplateManager();
    manager.value = data;
    if (!syncChannelId.value && syncableChannels.value.length) {
      syncChannelId.value = syncableChannels.value[0].id;
    }
  } finally {
    isLoading.value = false;
  }
};

const openStorageSettings = () => {
  const storage = manager.value.storage || {};
  storageForm.value = {
    enabled: Boolean(storage.enabled),
    project_id: storage.project_id || '',
    bucket_name: storage.bucket_name || '',
    credentials_json: '',
  };
  credentialsFileName.value = '';
  credentialsClientEmail.value = storage.credentials_client_email || '';
  showEditor.value = false;
  showStorageSettings.value = true;
};

const closeStorageSettings = () => {
  showStorageSettings.value = false;
  storageForm.value = emptyStorageForm();
  credentialsFileName.value = '';
  credentialsClientEmail.value = '';
};

const onCredentialsSelected = async event => {
  const [file] = event.target.files;
  storageForm.value.credentials_json = '';
  credentialsFileName.value = '';
  if (!file) return;

  if (file.size > 64 * 1024) {
    useAlert(t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.JSON_TOO_LARGE'));
    event.target.value = '';
    return;
  }

  try {
    const rawCredentials = await file.text();
    const credentials = JSON.parse(rawCredentials);
    if (
      credentials.type !== 'service_account' ||
      !credentials.project_id ||
      !credentials.client_email ||
      !credentials.private_key
    ) {
      throw new Error('invalid service account');
    }

    storageForm.value.credentials_json = rawCredentials;
    storageForm.value.project_id = credentials.project_id;
    credentialsFileName.value = file.name;
    credentialsClientEmail.value = credentials.client_email;
  } catch {
    useAlert(t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.JSON_INVALID'));
    event.target.value = '';
  }
};

const saveStorageSettings = async () => {
  const storage = storageForm.value;
  const alreadyConfigured = manager.value.storage?.credentials_configured;
  if (
    storage.enabled &&
    (!storage.project_id ||
      !storage.bucket_name ||
      (!alreadyConfigured && !storage.credentials_json))
  ) {
    useAlert(t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.REQUIRED'));
    return;
  }

  isSavingStorage.value = true;
  try {
    const payload = {
      enabled: storage.enabled,
      project_id: storage.project_id,
      bucket_name: storage.bucket_name,
    };
    if (storage.credentials_json) {
      payload.credentials_json = storage.credentials_json;
    }

    const { data } =
      await IntegrationsAPI.updateWhatsappTemplateStorage(payload);
    manager.value = data;
    closeStorageSettings();
    useAlert(t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.SAVED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.SAVE_ERROR')
    );
  } finally {
    isSavingStorage.value = false;
  }
};

const openNewTemplate = () => {
  resetMediaPreview();
  showStorageSettings.value = false;
  editingTemplateId.value = null;
  form.value = emptyForm();
  form.value.channel_id = creatableChannels.value[0]?.id || '';
  showEditor.value = true;
};

function headerTypeFor(template) {
  if (template.template_kind !== 'text') return template.template_kind;

  return template.configuration.header_text ? 'text' : 'none';
}

const openEditTemplate = template => {
  resetMediaPreview();
  showStorageSettings.value = false;
  editingTemplateId.value = template.id;
  form.value = {
    channel_id: template.channel_id,
    name: template.name,
    language: template.language,
    category: template.category,
    template_kind: template.template_kind,
    configuration: {
      template_type: template.configuration.template_type || 'default',
      header_type:
        template.configuration.header_type || headerTypeFor(template),
      parameter_format:
        template.configuration.parameter_format ||
        (/\{\{[a-z][a-z_]*\}\}/.test(
          `${template.configuration.header_text || ''} ${
            template.configuration.body || ''
          }`
        )
          ? PARAMETER_FORMATS.NAMED
          : PARAMETER_FORMATS.POSITIONAL),
      header_text: template.configuration.header_text || '',
      header_example: template.configuration.header_example || '',
      body: template.configuration.body || '',
      body_examples: [...(template.configuration.body_examples || [])],
      footer: template.configuration.footer || '',
      buttons: (template.configuration.buttons || []).map(button => ({
        ...button,
      })),
      flow_id: template.configuration.flow_id || '',
      navigate_screen: template.configuration.navigate_screen || '',
      flow_action: template.configuration.flow_action || 'navigate',
      flow_cta: template.configuration.flow_cta || '',
    },
  };
  localMediaUrl.value = template.sample_media?.url || '';
  showEditor.value = true;
};

const closeEditor = () => {
  resetMediaPreview();
  showEditor.value = false;
  editingTemplateId.value = null;
  form.value = emptyForm();
};

const openTemplatePreview = template => {
  selectedPreviewTemplate.value = template;
  nextTick(() => previewDialogRef.value?.open());
};

const closeTemplatePreview = () => {
  previewDialogRef.value?.close();
};

const clearTemplatePreview = () => {
  selectedPreviewTemplate.value = null;
};

const onFileSelected = event => {
  resetMediaPreview();
  const [file] = event.target.files;
  if (!file) return;

  const error = mediaFileError(file, form.value.template_kind);
  if (error) {
    const errors = {
      type: t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_ERRORS.TYPE'),
      size: t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_ERRORS.SIZE'),
      empty: t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_ERRORS.EMPTY'),
    };
    mediaValidationError.value = errors[error];
    event.target.value = '';
    return;
  }

  selectedFile.value = file;
  localMediaUrl.value = URL.createObjectURL(file);
};

const uploadSampleMedia = async () => {
  if (!selectedFile.value) return null;

  if (manager.value.storage?.enabled) {
    return new Promise((resolve, reject) => {
      const query = new URLSearchParams({
        channel_id: form.value.channel_id,
        template_kind: form.value.template_kind,
      });
      const upload = new DirectUpload(
        selectedFile.value,
        `/api/v1/accounts/${accountId.value}/integrations/whatsapp_template_manager/direct_upload?${query.toString()}`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            setDirectUploadAuthHeaders(xhr);
          },
        }
      );
      upload.create((error, blob) => {
        if (error) {
          reject(
            new Error(
              t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_ERRORS.DIRECT_UPLOAD')
            )
          );
        } else {
          resolve(blob.signed_id);
        }
      });
    });
  }

  const payload = new FormData();
  payload.append('attachment', selectedFile.value);
  const { data } = await axios.post(
    `/api/v1/accounts/${accountId.value}/upload`,
    payload
  );
  return data.blob_id;
};

const templatePayload = sampleMediaBlobId => ({
  ...form.value,
  sample_media_blob_id: sampleMediaBlobId,
  configuration: {
    ...form.value.configuration,
    buttons: form.value.configuration.buttons.map(button => ({ ...button })),
  },
});

const saveTemplate = async submitAfterSave => {
  if (formValidationMessage.value) {
    useAlert(formValidationMessage.value);
    return;
  }
  if (mediaValidationError.value) {
    useAlert(mediaValidationError.value);
    return;
  }
  if (
    submitAfterSave &&
    form.value.template_kind !== 'text' &&
    !selectedFile.value &&
    !localMediaUrl.value
  ) {
    useAlert(t('WHATSAPP_TEMPLATE_MANAGER.FORM.ERRORS.MEDIA_REQUIRED'));
    return;
  }

  isSaving.value = true;
  try {
    const blobId = await uploadSampleMedia();
    const payload = templatePayload(blobId);
    const response = editingTemplateId.value
      ? await IntegrationsAPI.updateWhatsappTemplate(
          editingTemplateId.value,
          payload
        )
      : await IntegrationsAPI.createWhatsappTemplate(payload);

    if (submitAfterSave) {
      await IntegrationsAPI.submitWhatsappTemplate(response.data.id);
      useAlert(t('WHATSAPP_TEMPLATE_MANAGER.FORM.SUBMITTED'));
    } else {
      useAlert(t('WHATSAPP_TEMPLATE_MANAGER.FORM.SAVED'));
    }
    closeEditor();
    await fetchManager();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        (submitAfterSave
          ? t('WHATSAPP_TEMPLATE_MANAGER.FORM.SUBMIT_ERROR')
          : t('WHATSAPP_TEMPLATE_MANAGER.FORM.SAVE_ERROR'))
    );
  } finally {
    isSaving.value = false;
  }
};

const submitTemplate = async template => {
  isSaving.value = true;
  try {
    await IntegrationsAPI.submitWhatsappTemplate(template.id);
    useAlert(t('WHATSAPP_TEMPLATE_MANAGER.FORM.SUBMITTED'));
    await fetchManager();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATE_MANAGER.FORM.SUBMIT_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const deleteTemplate = async template => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('WHATSAPP_TEMPLATE_MANAGER.FORM.DELETE_CONFIRM'))) {
    return;
  }

  try {
    await IntegrationsAPI.deleteWhatsappTemplate(template.id);
    await fetchManager();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('WHATSAPP_TEMPLATE_MANAGER.FORM.SAVE_ERROR')
    );
  }
};

const syncTemplates = async () => {
  if (!syncChannelId.value) return;

  isSyncing.value = true;
  try {
    await IntegrationsAPI.syncWhatsappTemplates(syncChannelId.value);
    useAlert(t('WHATSAPP_TEMPLATE_MANAGER.LIST.SYNCED'));
    await fetchManager();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATE_MANAGER.LIST.SYNC_ERROR')
    );
  } finally {
    isSyncing.value = false;
  }
};

const addButton = () => {
  if (form.value.configuration.buttons.length >= MAX_TEMPLATE_BUTTONS) return;
  form.value.configuration.buttons.push({
    type: 'QUICK_REPLY',
    text: '',
    url: '',
    phone_number: '',
    example: '',
  });
};

const removeButton = index => {
  form.value.configuration.buttons.splice(index, 1);
};

const selectTemplateType = option => {
  if (option.disabled) return;

  form.value.configuration.template_type = option.value;
};

const updateBodySelection = result => {
  form.value.configuration.body = result.value.slice(0, 1024);
  showEmojiPicker.value = false;
  nextTick(() => {
    const textarea = document.getElementById('whatsapp-template-body');
    if (!textarea) return;

    textarea.focus();
    textarea.setSelectionRange(result.selectionStart, result.selectionEnd);
  });
};

const insertIntoBody = ({ prefix, suffix = '', placeholder = '' }) => {
  const textarea = document.getElementById('whatsapp-template-body');
  const value = form.value.configuration.body;
  const selectionStart = textarea?.selectionStart ?? value.length;
  const selectionEnd = textarea?.selectionEnd ?? value.length;
  updateBodySelection(
    insertAtSelection({
      value,
      selectionStart,
      selectionEnd,
      prefix,
      suffix,
      placeholder,
    })
  );
};

const insertBodyVariable = () => {
  insertIntoBody({
    prefix: nextBodyVariable(
      form.value.configuration.body,
      parameterFormat.value
    ),
  });
};

const updateParameterFormat = newFormat => {
  if (newFormat === parameterFormat.value) return;

  const configuration = form.value.configuration;
  configuration.body = convertVariableFormat(configuration.body, newFormat);
  configuration.header_text = convertVariableFormat(
    configuration.header_text,
    newFormat
  );
  configuration.parameter_format = newFormat;
};

const insertHeaderVariable = () => {
  if (headerHasVariable.value) return;

  const variable =
    parameterFormat.value === PARAMETER_FORMATS.NAMED
      ? '{{header_name}}'
      : '{{1}}';
  form.value.configuration.header_text =
    `${form.value.configuration.header_text} ${variable}`.trim();
};

const insertEmoji = emoji => {
  insertIntoBody({ prefix: emoji });
};

const variableExampleLabel = variable =>
  t('WHATSAPP_TEMPLATE_MANAGER.FORM.VARIABLE_EXAMPLE', {
    variable: `{{${variable}}}`,
  });

const statusLabel = status => {
  const labels = {
    draft: t('WHATSAPP_TEMPLATE_MANAGER.STATUS.DRAFT'),
    submitting: t('WHATSAPP_TEMPLATE_MANAGER.STATUS.SUBMITTING'),
    pending: t('WHATSAPP_TEMPLATE_MANAGER.STATUS.PENDING'),
    approved: t('WHATSAPP_TEMPLATE_MANAGER.STATUS.APPROVED'),
    rejected: t('WHATSAPP_TEMPLATE_MANAGER.STATUS.REJECTED'),
    paused: t('WHATSAPP_TEMPLATE_MANAGER.STATUS.PAUSED'),
    disabled: t('WHATSAPP_TEMPLATE_MANAGER.STATUS.DISABLED'),
    deleted: t('WHATSAPP_TEMPLATE_MANAGER.STATUS.DELETED'),
    failed: t('WHATSAPP_TEMPLATE_MANAGER.STATUS.FAILED'),
  };
  return labels[status] || status;
};

const typeLabel = type => {
  const labels = {
    text: t('WHATSAPP_TEMPLATE_MANAGER.TYPES.TEXT'),
    image: t('WHATSAPP_TEMPLATE_MANAGER.TYPES.IMAGE'),
    video: t('WHATSAPP_TEMPLATE_MANAGER.TYPES.VIDEO'),
    document: t('WHATSAPP_TEMPLATE_MANAGER.TYPES.DOCUMENT'),
  };
  return labels[type] || type;
};

const categoryLabel = category => {
  const labels = {
    UTILITY: t('WHATSAPP_TEMPLATE_MANAGER.CATEGORIES.UTILITY'),
    MARKETING: t('WHATSAPP_TEMPLATE_MANAGER.CATEGORIES.MARKETING'),
    AUTHENTICATION: t('WHATSAPP_TEMPLATE_MANAGER.CATEGORIES.AUTHENTICATION'),
  };
  return labels[category] || category;
};

const statusClasses = status => {
  const classes = {
    approved: 'bg-n-teal-3 text-n-teal-11',
    pending: 'bg-n-amber-3 text-n-amber-11',
    submitting: 'bg-n-amber-3 text-n-amber-11',
    rejected: 'bg-n-ruby-3 text-n-ruby-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    draft: 'bg-n-slate-3 text-n-slate-11',
  };
  return classes[status] || 'bg-n-slate-3 text-n-slate-11';
};

onMounted(fetchManager);
onBeforeUnmount(resetMediaPreview);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('WHATSAPP_TEMPLATE_MANAGER.TITLE')"
        :description="$t('WHATSAPP_TEMPLATE_MANAGER.DESCRIPTION')"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      >
        <template #actions>
          <Button
            v-if="manager.administrator"
            :label="$t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.ACTION')"
            icon="i-lucide-cloud"
            slate
            outline
            sm
            @click="openStorageSettings"
          />
          <Button
            v-if="creatableChannels.length"
            :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.NEW')"
            icon="i-lucide-plus"
            sm
            @click="openNewTemplate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col w-full gap-5">
        <template v-if="showStorageSettings">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="mb-0 text-heading-2 text-n-slate-12">
                {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.TITLE') }}
              </h2>
              <p class="mt-1 mb-0 text-sm text-n-slate-10">
                {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.DESCRIPTION') }}
              </p>
            </div>
            <Button
              :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.BACK')"
              icon="i-lucide-arrow-left"
              slate
              ghost
              sm
              @click="closeStorageSettings"
            />
          </div>

          <div
            class="flex flex-col max-w-3xl gap-5 p-5 border rounded-xl border-n-weak bg-n-card"
          >
            <label
              class="flex items-start justify-between gap-4 p-4 border rounded-xl cursor-pointer border-n-weak"
            >
              <span>
                <span class="block font-medium text-n-slate-12">
                  {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.ENABLE') }}
                </span>
                <span class="block mt-1 text-sm text-n-slate-10">
                  {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.ENABLE_HELP') }}
                </span>
              </span>
              <input
                v-model="storageForm.enabled"
                type="checkbox"
                class="mt-1 size-4 accent-n-brand"
              />
            </label>

            <template v-if="storageForm.enabled">
              <div class="grid gap-4 sm:grid-cols-2">
                <Input
                  v-model="storageForm.project_id"
                  :label="$t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.PROJECT_ID')"
                  autocomplete="off"
                />
                <Input
                  v-model="storageForm.bucket_name"
                  :label="$t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.BUCKET')"
                  autocomplete="off"
                />
              </div>

              <div class="flex flex-col gap-2">
                <label class="text-heading-3 text-n-slate-12">
                  {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.CREDENTIALS') }}
                </label>
                <input
                  type="file"
                  accept=".json,application/json"
                  class="block w-full text-sm rounded-lg cursor-pointer text-n-slate-11 file:mr-3 file:border-0 file:px-3 file:py-2 file:bg-n-alpha-3 file:text-n-slate-12"
                  @change="onCredentialsSelected"
                />
                <p class="mb-0 text-sm text-n-slate-10">
                  {{
                    manager.storage?.credentials_configured &&
                    !credentialsFileName
                      ? $t(
                          'WHATSAPP_TEMPLATE_MANAGER.STORAGE.CREDENTIALS_CONFIGURED',
                          {
                            email:
                              manager.storage.credentials_client_email || '—',
                          }
                        )
                      : credentialsFileName
                        ? $t(
                            'WHATSAPP_TEMPLATE_MANAGER.STORAGE.CREDENTIALS_SELECTED',
                            {
                              filename: credentialsFileName,
                              email: credentialsClientEmail,
                            }
                          )
                        : $t(
                            'WHATSAPP_TEMPLATE_MANAGER.STORAGE.CREDENTIALS_HELP'
                          )
                  }}
                </p>
              </div>

              <div
                class="flex gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-2"
              >
                <span class="i-lucide-shield-check mt-0.5 size-5 shrink-0" />
                <div class="text-sm text-n-slate-11">
                  <p class="mb-1 font-medium text-n-slate-12">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.SECURITY_TITLE') }}
                  </p>
                  <p class="mb-0">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.SECURITY_HELP') }}
                  </p>
                </div>
              </div>

              <div
                class="flex gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-2"
              >
                <span class="i-lucide-code-2 mt-0.5 size-5 shrink-0" />
                <div class="text-sm text-n-slate-11">
                  <p class="mb-1 font-medium text-n-slate-12">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.CORS_TITLE') }}
                  </p>
                  <p class="mb-2">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.CORS_HELP') }}
                  </p>
                  <code
                    class="block p-2 overflow-x-auto text-xs rounded bg-n-alpha-3 text-n-slate-12"
                  >
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.CORS_EXAMPLE') }}
                  </code>
                </div>
              </div>
            </template>

            <div class="flex justify-end gap-3">
              <Button
                :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.CANCEL')"
                slate
                outline
                @click="closeStorageSettings"
              />
              <Button
                :label="$t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.SAVE')"
                :is-loading="isSavingStorage"
                @click="saveStorageSettings"
              />
            </div>
          </div>
        </template>

        <template v-else-if="showEditor">
          <div class="flex items-center justify-between">
            <h2 class="text-heading-2 text-n-slate-12">
              {{ editorTitle }}
            </h2>
            <Button
              :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.BACK')"
              icon="i-lucide-arrow-left"
              slate
              ghost
              sm
              @click="closeEditor"
            />
          </div>

          <div
            class="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_340px]"
          >
            <div
              class="flex flex-col gap-5 p-5 border rounded-xl border-n-weak bg-n-card"
            >
              <div class="grid gap-4 sm:grid-cols-2">
                <div class="flex flex-col gap-1">
                  <label class="text-heading-3 text-n-slate-12">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.CHANNEL') }}
                  </label>
                  <Select
                    v-model="form.channel_id"
                    :options="channelOptions"
                    :disabled="Boolean(editingTemplateId)"
                    :placeholder="
                      $t('WHATSAPP_TEMPLATE_MANAGER.FORM.CHANNEL_PLACEHOLDER')
                    "
                    class="w-full [&>select]:w-full"
                  />
                </div>
                <Input
                  v-model="form.name"
                  :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.NAME')"
                  :message="nameValidationMessage"
                  :message-type="hasNameError ? 'error' : 'info'"
                  :disabled="Boolean(editingTemplateId)"
                  maxlength="512"
                />
                <div class="flex flex-col gap-1">
                  <label class="text-heading-3 text-n-slate-12">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.LANGUAGE') }}
                  </label>
                  <Select
                    v-model="form.language"
                    :options="languageOptions"
                    :disabled="Boolean(editingTemplateId)"
                    class="w-full [&>select]:w-full"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-heading-3 text-n-slate-12">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.CATEGORY') }}
                  </label>
                  <Select
                    v-model="form.category"
                    :options="categoryOptions"
                    :disabled="
                      ['catalog', 'call_permission_request'].includes(
                        form.configuration.template_type
                      )
                    "
                    class="w-full [&>select]:w-full"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-3">
                <div>
                  <h3 class="mb-0 text-heading-3 text-n-slate-12">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.TEMPLATE_TYPE') }}
                  </h3>
                  <p class="mt-1 mb-0 text-sm text-n-slate-10">
                    {{
                      $t(
                        'WHATSAPP_TEMPLATE_MANAGER.FORM.TEMPLATE_TYPE_DESCRIPTION'
                      )
                    }}
                  </p>
                </div>
                <div class="grid gap-3 sm:grid-cols-2">
                  <button
                    v-for="option in templateTypeOptions"
                    :key="option.value"
                    type="button"
                    class="flex gap-3 p-4 text-left transition-colors border rounded-xl"
                    :class="[
                      form.configuration.template_type === option.value
                        ? 'border-n-brand bg-n-blue-2'
                        : 'border-n-weak hover:border-n-slate-6',
                      option.disabled
                        ? 'cursor-not-allowed opacity-55'
                        : 'cursor-pointer',
                    ]"
                    :disabled="option.disabled"
                    @click="selectTemplateType(option)"
                  >
                    <span :class="option.icon" class="mt-0.5 size-5 shrink-0" />
                    <span>
                      <span class="block font-medium text-n-slate-12">
                        {{ option.title }}
                      </span>
                      <span class="block mt-1 text-xs text-n-slate-10">
                        {{ option.description }}
                      </span>
                    </span>
                  </button>
                </div>
              </div>

              <div class="pt-5 border-t border-n-weak">
                <h3 class="mb-0 text-heading-2 text-n-slate-12">
                  {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.CONTENT') }}
                </h3>
                <p class="mt-1 mb-0 text-sm text-n-slate-10">
                  {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.CONTENT_DESCRIPTION') }}
                </p>
              </div>

              <div class="grid gap-4 sm:grid-cols-2">
                <div class="flex flex-col gap-1">
                  <label class="text-heading-3 text-n-slate-12">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.VARIABLE_TYPE') }}
                  </label>
                  <Select
                    :model-value="form.configuration.parameter_format"
                    :options="parameterFormatOptions"
                    class="w-full [&>select]:w-full"
                    @update:model-value="updateParameterFormat"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-heading-3 text-n-slate-12">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.HEADER_TYPE') }}
                  </label>
                  <Select
                    v-model="form.configuration.header_type"
                    :options="headerTypeOptions"
                    :disabled="form.configuration.template_type !== 'default'"
                    class="w-full [&>select]:w-full"
                  />
                </div>
              </div>

              <template v-if="form.configuration.header_type === 'text'">
                <div class="flex items-end gap-2">
                  <Input
                    v-model="form.configuration.header_text"
                    :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.HEADER')"
                    maxlength="60"
                    class="flex-1"
                  />
                  <Button
                    v-tooltip.top="
                      $t(
                        'WHATSAPP_TEMPLATE_MANAGER.FORM.INSERT_HEADER_VARIABLE'
                      )
                    "
                    icon="i-lucide-braces"
                    slate
                    outline
                    :disabled="headerHasVariable"
                    @click="insertHeaderVariable"
                  />
                </div>
                <Input
                  v-if="headerHasVariable"
                  v-model="form.configuration.header_example"
                  :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.HEADER_EXAMPLE')"
                  :message="
                    $t('WHATSAPP_TEMPLATE_MANAGER.FORM.TEST_VALUE_HELP')
                  "
                />
              </template>

              <div
                v-else-if="form.template_kind !== 'text'"
                class="flex flex-col gap-2"
              >
                <label class="text-heading-3 text-n-slate-12">
                  {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA') }}
                  <span class="font-normal text-n-slate-10">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.OPTIONAL') }}
                  </span>
                </label>
                <input
                  type="file"
                  :accept="acceptedMediaTypes"
                  class="block w-full text-sm rounded-lg cursor-pointer text-n-slate-11 file:mr-3 file:border-0 file:px-3 file:py-2 file:bg-n-alpha-3 file:text-n-slate-12"
                  @change="onFileSelected"
                />
                <p
                  class="mb-0 text-sm"
                  :class="
                    mediaValidationError ? 'text-n-ruby-9' : 'text-n-slate-10'
                  "
                >
                  {{
                    mediaValidationError ||
                    `${$t(
                      'WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_HELP'
                    )} ${mediaReference}`
                  }}
                </p>
                <p
                  v-if="manager.storage?.enabled"
                  class="flex items-center gap-1 mb-0 text-xs text-n-teal-11"
                >
                  <span class="i-lucide-cloud-upload size-4" />
                  {{ $t('WHATSAPP_TEMPLATE_MANAGER.STORAGE.DIRECT_UPLOAD') }}
                </p>
              </div>

              <TextArea
                id="whatsapp-template-body"
                v-model="form.configuration.body"
                :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.BODY')"
                :message="bodyHelp"
                :max-length="1024"
                show-character-count
                auto-height
                resize
              >
                <div
                  class="flex flex-wrap items-center gap-1 pb-2 border-b border-n-weak"
                >
                  <div
                    v-on-click-outside="() => (showEmojiPicker = false)"
                    class="relative"
                  >
                    <Button
                      v-tooltip.top="
                        $t('WHATSAPP_TEMPLATE_MANAGER.FORM.INSERT_EMOJI')
                      "
                      icon="i-lucide-smile-plus"
                      color="slate"
                      variant="ghost"
                      size="sm"
                      @click="showEmojiPicker = !showEmojiPicker"
                    />
                    <EmojiIconPicker
                      v-if="showEmojiPicker"
                      mode="emoji"
                      class="!top-9 ltr:left-0 rtl:right-0"
                      @select="insertEmoji($event.value)"
                    />
                  </div>
                  <Button
                    v-tooltip.top="
                      $t('WHATSAPP_TEMPLATE_MANAGER.FORM.INSERT_VARIABLE')
                    "
                    icon="i-lucide-braces"
                    color="slate"
                    variant="ghost"
                    size="sm"
                    @click="insertBodyVariable"
                  />
                  <span class="h-5 mx-1 border-l border-n-weak" />
                  <Button
                    v-tooltip.top="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.BOLD')"
                    label="B"
                    color="slate"
                    variant="ghost"
                    size="sm"
                    class="font-bold"
                    @click="
                      insertIntoBody({
                        prefix: '*',
                        suffix: '*',
                        placeholder: $t(
                          'WHATSAPP_TEMPLATE_MANAGER.FORM.FORMAT_PLACEHOLDER'
                        ),
                      })
                    "
                  />
                  <Button
                    v-tooltip.top="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.ITALIC')"
                    label="I"
                    color="slate"
                    variant="ghost"
                    size="sm"
                    class="italic"
                    @click="
                      insertIntoBody({
                        prefix: '_',
                        suffix: '_',
                        placeholder: $t(
                          'WHATSAPP_TEMPLATE_MANAGER.FORM.FORMAT_PLACEHOLDER'
                        ),
                      })
                    "
                  />
                  <Button
                    v-tooltip.top="
                      $t('WHATSAPP_TEMPLATE_MANAGER.FORM.STRIKETHROUGH')
                    "
                    label="S"
                    color="slate"
                    variant="ghost"
                    size="sm"
                    class="line-through"
                    @click="
                      insertIntoBody({
                        prefix: '~',
                        suffix: '~',
                        placeholder: $t(
                          'WHATSAPP_TEMPLATE_MANAGER.FORM.FORMAT_PLACEHOLDER'
                        ),
                      })
                    "
                  />
                  <Button
                    v-tooltip.top="
                      $t('WHATSAPP_TEMPLATE_MANAGER.FORM.MONOSPACE')
                    "
                    icon="i-lucide-code-2"
                    color="slate"
                    variant="ghost"
                    size="sm"
                    @click="
                      insertIntoBody({
                        prefix: '```',
                        suffix: '```',
                        placeholder: $t(
                          'WHATSAPP_TEMPLATE_MANAGER.FORM.FORMAT_PLACEHOLDER'
                        ),
                      })
                    "
                  />
                </div>
              </TextArea>

              <div
                v-if="bodyVariables.length"
                class="grid gap-3 sm:grid-cols-2"
              >
                <Input
                  v-for="(variable, index) in bodyVariables"
                  :key="variable"
                  v-model="form.configuration.body_examples[index]"
                  :label="variableExampleLabel(variable)"
                  :message="
                    $t('WHATSAPP_TEMPLATE_MANAGER.FORM.TEST_VALUE_HELP')
                  "
                />
              </div>

              <Input
                v-model="form.configuration.footer"
                :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.FOOTER')"
                maxlength="60"
              />

              <div
                v-if="form.configuration.template_type === 'flow'"
                class="grid gap-3 p-4 border rounded-xl border-n-weak sm:grid-cols-2"
              >
                <Input
                  v-model="form.configuration.flow_id"
                  :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.FLOW_ID')"
                  :message="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.FLOW_ID_HELP')"
                />
                <Input
                  v-model="form.configuration.navigate_screen"
                  :label="
                    $t('WHATSAPP_TEMPLATE_MANAGER.FORM.FLOW_NAVIGATE_SCREEN')
                  "
                />
                <Input
                  v-model="form.configuration.flow_cta"
                  :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.FLOW_CTA')"
                  maxlength="25"
                  class="sm:col-span-2"
                />
              </div>

              <div
                v-if="form.configuration.template_type === 'catalog'"
                class="flex gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-2"
              >
                <span class="i-lucide-info mt-0.5 size-5 shrink-0" />
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.CATALOG_HELP') }}
                </p>
              </div>

              <div
                v-if="
                  form.configuration.template_type === 'call_permission_request'
                "
                class="flex gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-2"
              >
                <span class="i-lucide-phone-call mt-0.5 size-5 shrink-0" />
                <p class="mb-0 text-sm text-n-slate-11">
                  {{
                    $t('WHATSAPP_TEMPLATE_MANAGER.FORM.CALL_PERMISSION_HELP')
                  }}
                </p>
              </div>

              <div
                v-if="form.configuration.template_type === 'default'"
                class="flex flex-col gap-3"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <h3 class="mb-0 text-heading-3 text-n-slate-12">
                      {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.BUTTONS') }}
                    </h3>
                    <p class="mt-1 mb-0 text-xs text-n-slate-10">
                      {{
                        $t('WHATSAPP_TEMPLATE_MANAGER.FORM.BUTTON_COUNT', {
                          count: form.configuration.buttons.length,
                          maximum: MAX_TEMPLATE_BUTTONS,
                        })
                      }}
                    </p>
                  </div>
                  <Button
                    :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.ADD_BUTTON')"
                    :disabled="
                      form.configuration.buttons.length >= MAX_TEMPLATE_BUTTONS
                    "
                    icon="i-lucide-plus"
                    slate
                    outline
                    sm
                    @click="addButton"
                  />
                </div>

                <div
                  v-for="(button, index) in form.configuration.buttons"
                  :key="index"
                  class="grid gap-3 p-3 border rounded-lg border-n-weak sm:grid-cols-2"
                >
                  <div class="flex flex-col gap-1">
                    <label class="text-heading-3 text-n-slate-12">
                      {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.BUTTON_TYPE') }}
                    </label>
                    <Select
                      v-model="button.type"
                      :options="buttonTypeOptions"
                      class="w-full [&>select]:w-full"
                    />
                  </div>
                  <Input
                    v-if="
                      !['COPY_CODE', 'REQUEST_CONTACT_INFO'].includes(
                        button.type
                      )
                    "
                    v-model="button.text"
                    :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.BUTTON_TEXT')"
                    maxlength="25"
                  />
                  <Input
                    v-if="button.type === 'URL'"
                    v-model="button.url"
                    :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.BUTTON_URL')"
                  />
                  <Input
                    v-if="button.type === 'URL' && button.url.includes('{{1}}')"
                    v-model="button.example"
                    :label="
                      $t('WHATSAPP_TEMPLATE_MANAGER.FORM.BUTTON_URL_EXAMPLE')
                    "
                  />
                  <Input
                    v-if="button.type === 'PHONE_NUMBER'"
                    v-model="button.phone_number"
                    :label="$t('WHATSAPP_TEMPLATE_MANAGER.FORM.BUTTON_PHONE')"
                    placeholder="+50760000000"
                  />
                  <Input
                    v-if="button.type === 'COPY_CODE'"
                    v-model="button.example"
                    :label="
                      $t('WHATSAPP_TEMPLATE_MANAGER.FORM.BUTTON_CODE_EXAMPLE')
                    "
                    :message="
                      $t('WHATSAPP_TEMPLATE_MANAGER.FORM.TEST_VALUE_HELP')
                    "
                  />
                  <div class="flex items-end justify-end sm:col-span-2">
                    <Button
                      :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.REMOVE')"
                      icon="i-lucide-trash-2"
                      ruby
                      ghost
                      sm
                      @click="removeButton(index)"
                    />
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap justify-end gap-3 pt-2">
                <Button
                  :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.CANCEL')"
                  slate
                  outline
                  @click="closeEditor"
                />
                <Button
                  :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.SAVE_DRAFT')"
                  :is-loading="isSaving"
                  slate
                  solid
                  @click="saveTemplate(false)"
                />
                <Button
                  v-if="canSubmitSelectedChannel"
                  :label="
                    $t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.SAVE_AND_SUBMIT')
                  "
                  :is-loading="isSaving"
                  @click="saveTemplate(true)"
                />
              </div>
            </div>

            <div class="sticky top-0 flex flex-col gap-3">
              <h3 class="text-heading-3 text-n-slate-12">
                {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.PREVIEW') }}
              </h3>
              <div class="p-4 rounded-2xl bg-[#efeae2] dark:bg-n-slate-3">
                <div
                  class="p-3 ml-auto bg-[#d9fdd3] dark:bg-n-teal-3 rounded-lg shadow-sm max-w-[290px]"
                >
                  <img
                    v-if="form.template_kind === 'image' && localMediaUrl"
                    :src="localMediaUrl"
                    class="object-cover w-full h-36 mb-2 rounded-md"
                  />
                  <div
                    v-else-if="form.template_kind !== 'text'"
                    class="flex items-center justify-center h-24 mb-2 rounded-md bg-n-alpha-3 text-n-slate-10"
                  >
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_PREVIEW') }}
                  </div>
                  <p
                    v-if="form.configuration.header_text"
                    class="mb-1 font-semibold whitespace-pre-wrap text-n-slate-12"
                  >
                    {{ currentTemplatePreview.header }}
                  </p>
                  <!-- Content is HTML-escaped before WhatsApp markers are rendered. -->
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <p
                    class="mb-1 text-sm whitespace-pre-wrap text-n-slate-12 [&_code]:px-1 [&_code]:rounded [&_code]:bg-n-alpha-3"
                    v-html="previewBodyHtml"
                  />
                  <p
                    v-if="currentTemplatePreview.footer"
                    class="mb-0 text-xs text-n-slate-10"
                  >
                    {{ currentTemplatePreview.footer }}
                  </p>
                  <div
                    v-for="(button, index) in previewButtons"
                    :key="index"
                    class="pt-2 mt-2 text-sm text-center border-t border-n-weak text-n-blue-11"
                  >
                    {{ button }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            v-if="manager.channels.length"
            class="flex flex-wrap items-end justify-between gap-3"
          >
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-11">
                {{ $t('WHATSAPP_TEMPLATE_MANAGER.FILTERS.CHANNEL') }}
              </label>
              <Select
                v-model="filterChannelId"
                :options="filterChannelOptions"
                class="[&>select]:min-w-52"
              />
            </div>
            <div
              v-if="syncableChannels.length"
              class="flex flex-wrap items-center gap-3"
            >
              <Select
                v-model="syncChannelId"
                :options="syncChannelOptions"
                class="[&>select]:min-w-52"
              />
              <Button
                :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.SYNC')"
                :is-loading="isSyncing"
                icon="i-lucide-refresh-cw"
                slate
                outline
                sm
                @click="syncTemplates"
              />
            </div>
          </div>

          <div
            v-if="!manager.channels.length && !manager.administrator"
            class="p-10 text-center border rounded-xl border-n-weak bg-n-card"
          >
            <h2 class="text-heading-2 text-n-slate-12">
              {{ $t('WHATSAPP_TEMPLATE_MANAGER.NO_ACCESS.TITLE') }}
            </h2>
            <p class="mt-2 text-body-main text-n-slate-11">
              {{ $t('WHATSAPP_TEMPLATE_MANAGER.NO_ACCESS.DESCRIPTION') }}
            </p>
          </div>

          <div
            v-else-if="!templates.length"
            class="p-10 text-center border rounded-xl border-n-weak bg-n-card text-n-slate-11"
          >
            {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.EMPTY') }}
          </div>

          <div
            v-else
            class="overflow-x-auto border rounded-xl border-n-weak bg-n-card"
          >
            <table class="w-full text-sm">
              <thead class="border-b border-n-weak bg-n-alpha-2">
                <tr class="text-left text-n-slate-11">
                  <th class="px-4 py-3 font-medium">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.NAME') }}
                  </th>
                  <th class="px-4 py-3 font-medium">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.CHANNEL') }}
                  </th>
                  <th class="px-4 py-3 font-medium">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.CATEGORY') }}
                  </th>
                  <th class="px-4 py-3 font-medium">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.TYPE') }}
                  </th>
                  <th class="px-4 py-3 font-medium">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.STATUS') }}
                  </th>
                  <th class="px-4 py-3 font-medium">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.AUTHOR') }}
                  </th>
                  <th class="px-4 py-3 font-medium text-right">
                    {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.ACTIONS') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="template in templates" :key="template.id">
                  <td class="px-4 py-3">
                    <p class="mb-0 font-medium text-n-slate-12">
                      {{ template.name }}
                    </p>
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{ template.language }}
                    </p>
                    <p
                      v-if="template.rejection_reason"
                      class="max-w-xs mt-1 mb-0 text-xs text-n-ruby-10"
                    >
                      {{ template.rejection_reason }}
                    </p>
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ template.inbox_name }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ categoryLabel(template.category) }}
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ typeLabel(template.template_kind) }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="inline-flex px-2 py-1 text-xs font-medium rounded-md"
                      :class="statusClasses(template.status)"
                    >
                      {{ statusLabel(template.status) }}
                    </span>
                    <p
                      v-if="template.quality_score"
                      class="mt-1 mb-0 text-xs text-n-slate-10"
                    >
                      {{
                        $t('WHATSAPP_TEMPLATE_MANAGER.LIST.QUALITY', {
                          score: template.quality_score,
                        })
                      }}
                    </p>
                  </td>
                  <td class="px-4 py-3 text-n-slate-11">
                    {{ template.created_by?.name || '—' }}
                  </td>
                  <td class="px-4 py-3">
                    <div class="flex justify-end gap-1">
                      <Button
                        :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.PREVIEW')"
                        icon="i-lucide-eye"
                        slate
                        ghost
                        xs
                        @click="openTemplatePreview(template)"
                      />
                      <Button
                        v-if="template.permissions.can_edit"
                        :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.EDIT')"
                        icon="i-lucide-pencil"
                        slate
                        ghost
                        xs
                        @click="openEditTemplate(template)"
                      />
                      <Button
                        v-if="template.permissions.can_submit"
                        :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.SUBMIT')"
                        icon="i-lucide-send"
                        xs
                        @click="submitTemplate(template)"
                      />
                      <Button
                        v-if="template.permissions.can_delete"
                        :label="$t('WHATSAPP_TEMPLATE_MANAGER.ACTIONS.DELETE')"
                        icon="i-lucide-trash-2"
                        ruby
                        ghost
                        xs
                        @click="deleteTemplate(template)"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>

      <Dialog
        ref="previewDialogRef"
        :title="previewDialogTitle"
        :show-cancel-button="false"
        :show-confirm-button="false"
        width="lg"
        overflow-y-auto
        @close="clearTemplatePreview"
      >
        <div v-if="selectedPreviewTemplate" class="flex flex-col gap-5">
          <dl class="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt class="text-n-slate-10">
                {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.CHANNEL') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ selectedPreviewTemplate.inbox_name }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.LANGUAGE') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ selectedPreviewTemplate.language }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.CATEGORY') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ categoryLabel(selectedPreviewTemplate.category) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-10">
                {{ $t('WHATSAPP_TEMPLATE_MANAGER.LIST.STATUS') }}
              </dt>
              <dd class="font-medium text-n-slate-12">
                {{ statusLabel(selectedPreviewTemplate.status) }}
              </dd>
            </div>
          </dl>

          <div class="p-4 rounded-2xl bg-[#efeae2] dark:bg-n-slate-3">
            <div
              class="p-3 ml-auto bg-[#d9fdd3] dark:bg-n-teal-3 rounded-lg shadow-sm max-w-[340px]"
            >
              <img
                v-if="
                  selectedPreviewTemplate.template_kind === 'image' &&
                  selectedPreviewTemplate.sample_media?.url
                "
                :src="selectedPreviewTemplate.sample_media.url"
                :alt="selectedPreviewTemplate.sample_media.filename"
                class="object-cover w-full h-44 mb-2 rounded-md"
              />
              <video
                v-else-if="
                  selectedPreviewTemplate.template_kind === 'video' &&
                  selectedPreviewTemplate.sample_media?.url
                "
                :src="selectedPreviewTemplate.sample_media.url"
                class="w-full mb-2 rounded-md max-h-52"
                controls
              />
              <a
                v-else-if="
                  selectedPreviewTemplate.template_kind === 'document' &&
                  selectedPreviewTemplate.sample_media?.url
                "
                :href="selectedPreviewTemplate.sample_media.url"
                class="flex items-center gap-2 p-3 mb-2 rounded-md bg-n-alpha-3 text-n-blue-11"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span class="i-lucide-file-text size-5" />
                <span class="truncate">
                  {{
                    selectedPreviewTemplate.sample_media.filename ||
                    $t('WHATSAPP_TEMPLATE_MANAGER.PREVIEW.OPEN_MEDIA')
                  }}
                </span>
              </a>
              <div
                v-else-if="selectedPreviewTemplate.template_kind !== 'text'"
                class="flex items-center justify-center h-24 mb-2 rounded-md bg-n-alpha-3 text-n-slate-10"
              >
                {{ $t('WHATSAPP_TEMPLATE_MANAGER.FORM.MEDIA_PREVIEW') }}
              </div>
              <p
                v-if="selectedTemplatePreview.header"
                class="mb-1 font-semibold whitespace-pre-wrap text-n-slate-12"
              >
                {{ selectedTemplatePreview.header }}
              </p>
              <!-- Content is HTML-escaped before WhatsApp markers are rendered. -->
              <!-- eslint-disable-next-line vue/no-v-html -->
              <p
                class="mb-1 text-sm whitespace-pre-wrap text-n-slate-12 [&_code]:px-1 [&_code]:rounded [&_code]:bg-n-alpha-3"
                v-html="selectedPreviewBodyHtml"
              />
              <p
                v-if="selectedTemplatePreview.footer"
                class="mb-0 text-xs text-n-slate-10"
              >
                {{ selectedTemplatePreview.footer }}
              </p>
              <div
                v-for="(button, index) in selectedPreviewButtons"
                :key="index"
                class="pt-2 mt-2 text-sm text-center border-t border-n-weak text-n-blue-11"
              >
                {{ button }}
              </div>
            </div>
          </div>

          <div class="flex justify-end">
            <Button
              :label="$t('WHATSAPP_TEMPLATE_MANAGER.PREVIEW.CLOSE')"
              slate
              outline
              @click="closeTemplatePreview"
            />
          </div>
        </div>
      </Dialog>
    </template>
  </SettingsLayout>
</template>
